/**
 * Camunda 7.18 Incident REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/incident/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  Incident,
  IncidentQuery,
  CountResult,
  Batch,
} from '../types';

export class IncidentService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/incident';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get List
  // ============================================================================

  /**
   * Get a list of incidents
   */
  async getList(query?: IncidentQuery): Promise<Incident[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<Incident[]>(`${this.basePath}${params}`);
  }

  /**
   * Get incidents count
   */
  async getCount(query?: IncidentQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  // ============================================================================
  // Get Single
  // ============================================================================

  /**
   * Get incident by ID
   */
  async getById(id: string): Promise<Incident> {
    return this.client.get<Incident>(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // Resolve
  // ============================================================================

  /**
   * Resolve an incident
   */
  async resolve(id: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // Annotation
  // ============================================================================

  /**
   * Set annotation for an incident
   */
  async setAnnotation(id: string, annotation: string | null): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/annotation`, { annotation });
  }

  /**
   * Clear annotation for an incident
   */
  async clearAnnotation(id: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/${id}/annotation`);
  }

  // ============================================================================
  // Batch Operations
  // ============================================================================

  /**
   * Set retries for incidents async
   */
  async setRetriesAsync(dto: {
    incidentIds?: string[];
    incidentQuery?: IncidentQuery;
    retries: number;
  }): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/retries-async`, dto);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: IncidentQuery): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else if (typeof value === 'object') {
          params.append(key, JSON.stringify(value));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let incidentServiceInstance: IncidentService | null = null;

export function getIncidentService(): IncidentService {
  if (!incidentServiceInstance) {
    incidentServiceInstance = new IncidentService();
  }
  return incidentServiceInstance;
}

export default IncidentService;
